import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { CheckCircle, AlertCircle, Circle } from 'lucide-react';
import { theme } from '../../styles/theme';

/**
 * WizardStepper — horizontal step indicator with navigation.
 *
 * Props:
 *   steps          - Array<{ key: string, label: string, icon: ReactNode }>
 *   currentStep    - number (active step index)
 *   completedSteps - Set<number> (indices of completed steps)
 *   errorSteps     - Set<number> (indices of steps with errors)
 *   onStepClick    - (stepIndex: number) => void
 */
export default function WizardStepper({
  steps,
  currentStep,
  completedSteps,
  errorSteps,
  onStepClick,
}) {
  const { t } = useTranslation('campaigns');

  const getStepState = (index) => {
    if (errorSteps.has(index)) return 'error';
    if (completedSteps.has(index)) return 'completed';
    if (index === currentStep) return 'current';
    return 'default';
  };

  const isClickable = (index) => completedSteps.has(index) && index !== currentStep;

  const handleStepClick = (index) => {
    if (isClickable(index)) {
      onStepClick(index);
    }
  };

  const handleKeyDown = (e, index) => {
    if ((e.key === 'Enter' || e.key === ' ') && isClickable(index)) {
      e.preventDefault();
      onStepClick(index);
    }
  };

  const renderStepIcon = (index) => {
    const state = getStepState(index);
    if (state === 'completed') {
      return <CheckCircle size={20} />;
    }
    if (state === 'error') {
      return <AlertCircle size={20} />;
    }
    return steps[index].icon || <Circle size={20} />;
  };

  return (
    <StepperContainer role="navigation" aria-label={t('stepper.navLabel')}>
      {steps.map((step, index) => {
        const state = getStepState(index);
        const clickable = isClickable(index);

        return (
          <React.Fragment key={step.key}>
            {index > 0 && <Connector $completed={completedSteps.has(index - 1)} />}
            <StepItem
              $state={state}
              $clickable={clickable}
              onClick={() => handleStepClick(index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              tabIndex={clickable ? 0 : -1}
              role="button"
              aria-current={index === currentStep ? 'step' : undefined}
              aria-disabled={!clickable}
              aria-label={`${step.label}${state === 'completed' ? t('stepper.completedSuffix') : ''}${state === 'error' ? t('stepper.errorSuffix') : ''}`}
            >
              <StepIconWrapper $state={state}>
                {renderStepIcon(index)}
              </StepIconWrapper>
              <StepLabel $state={state}>{step.label}</StepLabel>
            </StepItem>
          </React.Fragment>
        );
      })}
    </StepperContainer>
  );
}

const StepperContainer = styled.nav`
  display: flex;
  align-items: center;
  gap: 0;
  padding: ${theme.spacing[3]} ${theme.spacing[2]};
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.border};
    border-radius: 2px;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: row;
    align-items: center;
    gap: 0;
    padding: ${theme.spacing[2]} ${theme.spacing[1]};
  }
`;

const Connector = styled.div`
  flex: 1;
  height: 2px;
  min-width: 16px;
  max-width: 48px;
  background: ${(p) => (p.$completed ? theme.colors.success : theme.colors.border)};
  transition: ${theme.transitions.base};
  flex-shrink: 0;
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border-radius: ${theme.radii.lg};
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  transition: ${theme.transitions.fast};
  user-select: none;
  flex-shrink: 0;

  ${(p) => p.$clickable && `
    &:hover {
      background: ${theme.colors.primaryLight};
    }

    &:focus-visible {
      outline: 2px solid ${theme.colors.primary};
      outline-offset: 2px;
    }
  `}
`;

const StepIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${theme.radii.full};
  flex-shrink: 0;
  transition: ${theme.transitions.fast};

  ${(p) => {
    switch (p.$state) {
      case 'completed':
        return `
          background: ${theme.colors.successLight};
          color: ${theme.colors.success};
        `;
      case 'error':
        return `
          background: ${theme.colors.warningLight};
          color: ${theme.colors.warning};
        `;
      case 'current':
        return `
          background: ${theme.colors.primaryLight};
          color: ${theme.colors.primary};
          box-shadow: 0 0 0 2px ${theme.colors.primary};
        `;
      default:
        return `
          background: ${theme.colors.bgSecondary};
          color: ${theme.colors.textMuted};
        `;
    }
  }}
`;

const StepLabel = styled.span`
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.medium};
  white-space: nowrap;
  transition: ${theme.transitions.fast};

  ${(p) => {
    switch (p.$state) {
      case 'completed':
        return `color: ${theme.colors.success};`;
      case 'error':
        return `color: ${theme.colors.warning};`;
      case 'current':
        return `
          color: ${theme.colors.primary};
          font-weight: ${theme.typography.semibold};
        `;
      default:
        return `color: ${theme.colors.textMuted};`;
    }
  }}
`;
